import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

import config from '@src/config';
import defaultClient from '@src/api/clients/default';
import { toast } from '@src/components/core/toaster';
import paths from '@src/paths';
import EmailRecipientsForm from '@src/pages/app/clients/components/EmailRecipientsForm';

const STEPS = ['Billing & Card', 'Lead Notifications', 'Done'];

const EMPTY_BILLING = {
  name: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
};

function fetchOnboarding(slug) {
  return defaultClient.request({ url: `clients/${slug}/onboarding/` });
}

function saveOnboarding(slug, payload) {
  return defaultClient.request({ url: `clients/${slug}/onboarding/`, method: 'PATCH', payload });
}

function fetchSetupIntentStatus(slug) {
  return defaultClient.request({ url: `app1/setup-intent/${slug}/` });
}

function createSetupIntent(slug) {
  return defaultClient.request({ url: `app1/setup-intent/${slug}/`, method: 'POST' });
}

export default function FiveStarOnboardingWizardPage() {
  const { clientSlug } = useParams();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');
  const [billingContact, setBillingContact] = useState(EMPTY_BILLING);
  const [cardStatus, setCardStatus] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [cardSaved, setCardSaved] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);

  // Initial load — confirm we're the right wizard, prefill billing
  useEffect(() => {
    if (!clientSlug) return;
    setLoading(true);
    Promise.all([
      fetchOnboarding(clientSlug).catch(() => null),
      fetchSetupIntentStatus(clientSlug).catch(() => null),
    ])
      .then(([onbRes, cardRes]) => {
        const data = onbRes?.data;
        if (data) {
          // Wrong wizard — bounce to standard onboarding
          if (data.application_source && data.application_source !== 'five_star') {
            navigate(paths.onboarding.forClient(clientSlug), { replace: true });
            return;
          }
          setClientName(data.client_name || '');
          if (data.billing_contact) {
            setBillingContact({ ...EMPTY_BILLING, ...data.billing_contact });
          }
        }
        const card = cardRes?.data || {};
        setCardStatus(card);
        if (card.has_card) setCardSaved(true);
        if (card.publishable_key) {
          setStripePromise((prev) => prev || loadStripe(card.publishable_key));
        }
      })
      .finally(() => setLoading(false));
  }, [clientSlug, navigate]);

  const updateBilling = (field) => (e) =>
    setBillingContact((prev) => ({ ...prev, [field]: e.target.value }));

  const billingValid = useMemo(
    () => Boolean(billingContact.name && billingContact.email),
    [billingContact],
  );

  const persistBilling = useCallback(async () => {
    if (!clientSlug) return;
    setSavingBilling(true);
    try {
      await saveOnboarding(clientSlug, { billing_contact: billingContact });
    } catch {
      toast.error('Failed to save billing info. Please try again.');
    } finally {
      setSavingBilling(false);
    }
  }, [clientSlug, billingContact]);

  const handleSetupCard = async () => {
    try {
      // Save billing contact first so Stripe customer gets created with the right info
      await persistBilling();
      const res = await createSetupIntent(clientSlug);
      setClientSecret(res?.data?.client_secret);
      if (res?.data?.publishable_key) {
        setStripePromise((prev) => prev || loadStripe(res.data.publishable_key));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to initialize card setup.');
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // Persist billing contact before advancing
      await persistBilling();
    }
    setActiveStep((p) => p + 1);
  };
  const handleBack = () => setActiveStep((p) => p - 1);

  const canProceed = useMemo(() => {
    if (activeStep === 0) return billingValid && cardSaved;
    return true;
  }, [activeStep, billingValid, cardSaved]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet><title>Onboarding | {config.SITE.title}</title></Helmet>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Welcome{clientName ? `, ${clientName}` : ''}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Two quick steps to finish setup — confirm billing and tell us who should receive lead notifications. Once done, our team launches your campaigns within 7–10 business days.
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            {activeStep === 0 && (
              <BillingStep
                billing={billingContact}
                updateBilling={updateBilling}
                billingValid={billingValid}
                cardSaved={cardSaved}
                cardStatus={cardStatus}
                stripePromise={stripePromise}
                clientSecret={clientSecret}
                onSetupCard={handleSetupCard}
                onCardSaved={(card) => {
                  setCardSaved(true);
                  setCardStatus((prev) => ({ ...prev, has_card: true, card }));
                  setClientSecret(null);
                }}
              />
            )}

            {activeStep === 1 && (
              <Stack spacing={2}>
                <Typography variant="h6">Lead Notifications</Typography>
                <EmailRecipientsForm
                  clientSlug={clientSlug}
                  helperText="Anyone you list here will receive an email for every lead that comes in for your branch. You can edit this list any time from your dashboard."
                />
              </Stack>
            )}

            {activeStep === 2 && <DoneStep />}
          </CardContent>
        </Card>

        <Stack direction="row" justifyContent="space-between">
          <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">
            Back
          </Button>
          {activeStep < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={!canProceed || savingBilling}
            >
              {activeStep === 0 ? 'Continue' : 'Next'}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<RocketLaunchIcon />}
              onClick={() => navigate(paths.clientDashboard(clientSlug))}
            >
              Go to Dashboard
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

function BillingStep({
  billing,
  updateBilling,
  billingValid,
  cardSaved,
  cardStatus,
  stripePromise,
  clientSecret,
  onSetupCard,
  onCardSaved,
}) {
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Billing & Payment Method</Typography>
      <Typography variant="body2" color="text.secondary">
        Who should we send invoices to, and what card should we keep on file? Your card will only be charged per the terms of your signed agreement.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField label="Billing Contact Name" value={billing.name} onChange={updateBilling('name')} fullWidth required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Billing Email" type="email" value={billing.email} onChange={updateBilling('email')} fullWidth required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Phone" value={billing.phone} onChange={updateBilling('phone')} fullWidth />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Address Line 1" value={billing.addressLine1} onChange={updateBilling('addressLine1')} fullWidth />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Address Line 2" value={billing.addressLine2} onChange={updateBilling('addressLine2')} fullWidth />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="City" value={billing.city} onChange={updateBilling('city')} fullWidth />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="State" value={billing.state} onChange={updateBilling('state')} fullWidth />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="ZIP Code" value={billing.postalCode} onChange={updateBilling('postalCode')} fullWidth />
        </Grid>
      </Grid>

      <Divider />

      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Payment Method</Typography>
        {cardSaved ? (
          <Alert severity="success" variant="outlined" icon={<CheckCircleIcon />}>
            <strong>Card on file:</strong>{' '}
            {cardStatus?.card ? (
              <>{cardStatus.card.brand?.toUpperCase()} ending in {cardStatus.card.last4} (exp {cardStatus.card.exp_month}/{cardStatus.card.exp_year})</>
            ) : (
              'Payment method saved'
            )}
          </Alert>
        ) : clientSecret && stripePromise ? (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
            <CardCollectionForm clientSecret={clientSecret} onSuccess={onCardSaved} />
          </Elements>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={onSetupCard}
              disabled={!billingValid}
              sx={{ textTransform: 'none' }}
            >
              Add Credit Card
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {billingValid
                ? 'Click to securely add a card. No charges will be made now.'
                : 'Fill in the billing contact name and email above first.'}
            </Typography>
          </>
        )}
      </Box>
    </Stack>
  );
}

function CardCollectionForm({ clientSecret, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);

    const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
      clientSecret,
      { payment_method: { card: elements.getElement(CardElement) } },
    );

    if (stripeError) {
      setError(stripeError.message);
      setSaving(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      try {
        const pm = await stripe.retrievePaymentMethod(setupIntent.payment_method);
        const card = pm?.paymentMethod?.card || {};
        onSuccess({
          brand: card.brand || '',
          last4: card.last4 || '',
          exp_month: card.exp_month,
          exp_year: card.exp_year,
        });
        toast.success('Card saved');
      } catch {
        onSuccess(null);
        toast.success('Card saved');
      }
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, bgcolor: '#fafafa' }}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#333',
                fontFamily: 'Arial, sans-serif',
                '::placeholder': { color: '#999' },
              },
              invalid: { color: '#dc2626' },
            },
          }}
        />
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{error}</Typography>
      )}
      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || saving}
          sx={{ bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' } }}
        >
          {saving ? 'Saving...' : 'Save Card'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          Secured by Stripe. No charges will be made now.
        </Typography>
      </Stack>
    </form>
  );
}

function DoneStep() {
  return (
    <Stack spacing={3} sx={{ textAlign: 'center', py: 4 }}>
      <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mx: 'auto' }} />
      <Typography variant="h5" fontWeight={700}>You're all set!</Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
        Your branch is ready to launch. Our team will set up your campaigns and you'll start receiving leads within 7–10 business days. We'll be in touch by email if we need anything else.
      </Typography>
    </Stack>
  );
}
